import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAdminConsultation, getAdminConsultationMessages, resolveMediaUrl } from '../../api';
import { PageHeader, StatusBadge, EmptyState, TableSkeleton, UserLink, AstrologerLink } from '../../components/ui';
import { formatCurrency, formatDateTime, formatDuration } from '../../utils/format';
import { ArrowLeft, MessageSquare, PhoneCall, Video, Clock, Wallet, User, Star } from 'lucide-react';

const TYPE_ICON: Record<string, React.ReactNode> = {
  chat: <MessageSquare size={16} />,
  call: <PhoneCall size={16} />,
  video: <Video size={16} />,
};

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="py-2">
      <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{label}</p>
      <p className="text-sm text-gray-900 mt-0.5 break-words">{value || '—'}</p>
    </div>
  );
}

export default function ConsultationDetailsPage() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-consultation', id],
    queryFn: () => getAdminConsultation(id!).then((r) => r.data.data),
    enabled: !!id,
  });

  const { data: msgData, isLoading: msgsLoading } = useQuery({
    queryKey: ['admin-consultation-msgs', id],
    queryFn: () => getAdminConsultationMessages(id!, { page: 1, limit: 200 }).then((r) => r.data),
    enabled: !!id,
  });

  const c = data?.consultation;
  const messages = msgData?.data || [];
  const user = c?.userId;
  const astro = c?.astrologerId;

  if (isLoading) {
    return <div className="p-6"><TableSkeleton rows={8} cols={2} /></div>;
  }
  if (!c) {
    return <div className="p-6"><EmptyState message="Consultation not found" /></div>;
  }

  const intake = c.intakeData || {};

  return (
    <div className="p-6">
      <PageHeader title="Consultation details" subtitle={`${c.type === 'call' ? 'AUDIO' : String(c.type || '').toUpperCase()} · ${c.status}`}>
        <Link to="/consultations" className="btn-secondary btn-sm flex items-center gap-1">
          <ArrowLeft size={14} /> Back
        </Link>
      </PageHeader>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 space-y-5">
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 font-semibold text-gray-900 capitalize">
                {TYPE_ICON[c.type]} {c.type === 'call' ? 'Audio' : c.type} session
              </div>
              <StatusBadge status={c.status} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Duration" value={c.duration ? formatDuration(c.duration) : '—'} />
              <Field label="Rate" value={formatCurrency(c.pricePerMinute) + '/min'} />
              <Field label="Min minutes" value={c.minDurationMinutes} />
              <Field label="Paid minutes" value={c.paidMinutes} />
              <Field label="Charged" value={formatCurrency(c.totalAmount || 0)} />
              <Field label="Platform fee" value={formatCurrency(c.platformFee || 0)} />
              <Field label="Astrologer earning" value={formatCurrency(c.astrologerEarnings || 0)} />
              <Field label="Wallet deducted" value={c.walletDeducted ? 'Yes' : 'No'} />
              <Field label="Ended by" value={c.endedBy || '—'} />
              <Field label="Started" value={c.startedAt ? formatDateTime(c.startedAt) : '—'} />
              <Field label="Ended" value={c.endedAt ? formatDateTime(c.endedAt) : '—'} />
              <Field label="Created" value={formatDateTime(c.createdAt)} />
              <Field label="Reviewed" value={c.isReviewed ? 'Yes' : 'No'} />
              <Field label="Cancel reason" value={c.cancelReason} />
              <Field label="Notes" value={c.notes} />
            </div>
          </div>

          <div className="card p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Intake form</h3>
            {intake.name || intake.question || intake.dateOfBirth ? (
              <div className="grid grid-cols-2 gap-4">
                <Field label="Name" value={intake.name} />
                <Field label="Topic" value={intake.topic} />
                <Field label="Date of birth" value={intake.dateOfBirth} />
                <Field label="Time of birth" value={intake.timeOfBirth} />
                <Field label="Place of birth" value={intake.placeOfBirth} />
                <div className="col-span-2"><Field label="Question" value={intake.question} /></div>
              </div>
            ) : (
              <p className="text-sm text-gray-400">No intake submitted.</p>
            )}
          </div>

          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Conversation</h3>
              <span className="text-xs text-gray-500">{data?.messageCount || messages.length} messages</span>
            </div>
            {c.type !== 'chat' && (
              <p className="text-xs text-gray-500 mb-3">Voice / video sessions may have few or no text messages.</p>
            )}
            {msgsLoading ? (
              <TableSkeleton rows={6} cols={1} />
            ) : messages.length === 0 ? (
              <EmptyState message="No messages in this session" icon={<MessageSquare size={28} />} />
            ) : (
              <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                {messages.map((m: any) => {
                  const fromAstro = m.senderType === 'astrologer';
                  return (
                    <div key={m._id} className={`flex ${fromAstro ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${fromAstro ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-800'}`}>
                        <p className="text-[10px] opacity-70 mb-0.5 capitalize">{m.senderType} · {m.type}</p>
                        {m.type === 'image' && m.mediaUrl
                          ? <img src={resolveMediaUrl(m.mediaUrl)} alt="" className="rounded-lg max-h-40" />
                          : m.type === 'video' && m.mediaUrl
                            ? <video src={resolveMediaUrl(m.mediaUrl)} controls className="rounded-lg max-h-40" />
                            : (m.type === 'audio' || m.type === 'voice') && m.mediaUrl
                              ? <audio src={resolveMediaUrl(m.mediaUrl)} controls />
                              : <p className="whitespace-pre-wrap">{m.content || '—'}</p>}
                        <p className={`text-[10px] mt-1 ${fromAstro ? 'text-white/70' : 'text-gray-400'}`}>{formatDateTime(m.createdAt)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-5">
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-3 text-gray-900 font-semibold"><User size={15} /> User</div>
            <div className="flex items-center gap-3">
              {user?.profileImage
                ? <img src={resolveMediaUrl(user.profileImage)} alt="" className="w-12 h-12 rounded-full object-cover" />
                : <div className="w-12 h-12 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold">{user?.name?.[0] || 'U'}</div>}
              <div>
                <UserLink entity={user} className="text-sm font-semibold text-primary-600 hover:underline block">
                  {user?.name || '—'}
                </UserLink>
                <p className="text-xs text-gray-500">{user?.phone}</p>
              </div>
            </div>
          </div>

          <div className="card p-5">
            <div className="flex items-center gap-2 mb-3 text-gray-900 font-semibold"><Star size={15} /> Astrologer</div>
            <div className="flex items-center gap-3">
              {astro?.profileImage
                ? <img src={resolveMediaUrl(astro.profileImage)} alt="" className="w-12 h-12 rounded-full object-cover" />
                : <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold">{astro?.name?.[0] || 'A'}</div>}
              <div>
                <AstrologerLink entity={astro} className="text-sm font-semibold text-primary-600 hover:underline block">
                  {astro?.name || '—'}
                </AstrologerLink>
                <p className="text-xs text-gray-500">{astro?.phone}</p>
              </div>
            </div>
          </div>

          <div className="card p-5 space-y-1">
            <div className="flex items-center gap-2 font-semibold text-gray-900 mb-2"><Wallet size={15} /> Billing snapshot</div>
            <Field label="Reserved" value={formatCurrency(c.reservedAmount || 0)} />
            <Field label="Expires" value={c.expiresAt ? formatDateTime(c.expiresAt) : '—'} />
            <Field label="Room" value={c.callData?.roomId || c.callData?.channelName} />
            <div className="flex items-center gap-1 text-xs text-gray-400 pt-2"><Clock size={12} /> ID {c._id}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
